import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { requestAllJanesConfig } from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/RequestTeamMemberViewDataProvider.constants';
import { useSSFBehaviors } from 'hooks';
import { SSFBehaviorsContextType } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { useAuthRouteDataContext } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { FC, useContext, useMemo } from 'react';
import { Jane, JaneConnection } from 'types/graphql';
import { createGQLDataContext } from 'utils/createDataContext';

interface TransformedJaneNode extends Jane {
  sourceType: 'marketplace' | 'agency';
}

interface TransformedRequestAllJanesData {
  requestAllJanes: Omit<JaneConnection, 'nodes'> & {
    nodes: TransformedJaneNode[];
  };
}

export const FetchTeamMembersDataContext = createGQLDataContext<
  SSFBehaviorsContextType<TransformedRequestAllJanesData>
>({} as SSFBehaviorsContextType<TransformedRequestAllJanesData>);

export const FetchTeamMembersDataProvider: FC = ({ children }) => {
  const { data } = useShiftModalDataContext();
  const order = data!.order;

  const {
    locationFilter: { initialValue: facilityId },
  } = useAuthRouteDataContext();

  const [values, rest] = useSSFBehaviors(
    requestAllJanesConfig({
      facilityId: Number(facilityId.value),
      orderId: order.relayId,
      positionId: order.position.displayId,
      communityId: order.community.id,
      recurring: order.recurrence
        ? {
            startTime: order.startAt,
            endTime: order.endAt,
            frequency: order.recurrence.frequency!,
            interval: order.recurrence.interval!,
            expirationDate: order.recurrence.expirationDate,
          }
        : undefined,
      breakTime: order.orderSchedule?.breakTime,
    }),
  );

  const transformedData = useMemo(() => {
    const rawData = rest.data as Record<string, JaneConnection> | undefined;
    const requestAllJanes = rawData?.requestAllJanes;
    if (!requestAllJanes) return undefined;

    const transformedNodes: TransformedJaneNode[] = requestAllJanes.nodes.map((node) => {
      const isMarketplace = !!node.positions?.[0]?.communityDetails?.chargeRate;

      return {
        ...node,
        sourceType: isMarketplace ? ('marketplace' as const) : ('agency' as const),
      };
    });

    return {
      requestAllJanes: {
        ...requestAllJanes,
        nodes: transformedNodes,
      },
    };
  }, [rest.data]);

  const contextValue = useMemo(
    () => ({ values, ...rest, data: transformedData as TransformedRequestAllJanesData }),
    [rest, values, transformedData],
  );

  return (
    <FetchTeamMembersDataContext.Provider value={contextValue}>
      {children}
    </FetchTeamMembersDataContext.Provider>
  );
};

export const useFetchTeamMembersDataContext =
  (): SSFBehaviorsContextType<TransformedRequestAllJanesData> =>
    useContext(FetchTeamMembersDataContext);
